import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { Asset } from "../types/imobi";
import { db } from "./firebase";

export type ResponsibilityHistoryRecord = {
  id: string;
  assetId: string;
  transferRequestId?: string;
  fromUserId?: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  requestedByName: string;
  authorizedByName: string;
  acceptedByName: string;
  conditionAtTransfer?: Asset["conservationState"];
  inspectionId?: string;
  startedAt: string;
  createdAt: string;
};

export type ResponsibilityTransferConditionInput = {
  conditionAtTransfer?: Asset["conservationState"];
  inspectionId?: string;
};

function mapResponsibilityHistory(assetId: string, id: string, data: Record<string, unknown>): ResponsibilityHistoryRecord {
  return {
    id,
    assetId,
    transferRequestId: data.transferRequestId ? String(data.transferRequestId) : undefined,
    fromUserId: data.fromUserId ? String(data.fromUserId) : undefined,
    fromUserName: String(data.fromUserName ?? ""),
    toUserId: String(data.toUserId ?? ""),
    toUserName: String(data.toUserName ?? ""),
    requestedByName: String(data.requestedByName ?? ""),
    authorizedByName: String(data.authorizedByName ?? ""),
    acceptedByName: String(data.acceptedByName ?? ""),
    conditionAtTransfer: data.conditionAtTransfer ? (data.conditionAtTransfer as Asset["conservationState"]) : undefined,
    inspectionId: data.inspectionId ? String(data.inspectionId) : undefined,
    startedAt: serializeTimestamp(data.startedAt),
    createdAt: serializeTimestamp(data.createdAt)
  };
}

export function subscribeAssetResponsibilityHistory(companyId: string | undefined, assetId: string | undefined, onChange: (records: ResponsibilityHistoryRecord[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyId || !assetId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "companies", companyId, "assets", assetId, "responsibilityHistory"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((historyDoc) => mapResponsibilityHistory(assetId, historyDoc.id, historyDoc.data()))),
    (error) => onError(error.message)
  );
}

export type ResponsibilityTransferStatus =
  | "aguardando_aprovacao"
  | "aguardando_confirmacao"
  | "concluida"
  | "recusada";

export type ResponsibilityTransferRequest = {
  id: string;
  companyId: string;
  assetIds: string[];
  assetPatrimonyNumbers: string[];
  fromUserId?: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  toUserEmail?: string;
  status: ResponsibilityTransferStatus;
  requiresManagerApproval: boolean;
  requestedBy: string;
  requestedByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  acceptedBy?: string;
  acceptedByName?: string;
  acceptedAt?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
};

export type CreateResponsibilityTransferInput = {
  companyId: string;
  assets: Asset[];
  fromUserId?: string;
  fromUserName: string;
  toUserId: string;
  toUserName: string;
  toUserEmail?: string;
  requiresManagerApproval: boolean;
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User) {
  return user.displayName ?? user.email ?? user.uid;
}

function mapTransfer(id: string, data: Record<string, unknown>): ResponsibilityTransferRequest {
  return {
    id,
    companyId: String(data.companyId ?? ""),
    assetIds: Array.isArray(data.assetIds) ? data.assetIds.map(String) : [],
    assetPatrimonyNumbers: Array.isArray(data.assetPatrimonyNumbers) ? data.assetPatrimonyNumbers.map(String) : [],
    fromUserId: data.fromUserId ? String(data.fromUserId) : undefined,
    fromUserName: String(data.fromUserName ?? ""),
    toUserId: String(data.toUserId ?? ""),
    toUserName: String(data.toUserName ?? ""),
    toUserEmail: data.toUserEmail ? String(data.toUserEmail) : undefined,
    status: (data.status ?? "aguardando_confirmacao") as ResponsibilityTransferStatus,
    requiresManagerApproval: Boolean(data.requiresManagerApproval),
    requestedBy: String(data.requestedBy ?? ""),
    requestedByName: String(data.requestedByName ?? ""),
    approvedBy: data.approvedBy ? String(data.approvedBy) : undefined,
    approvedByName: data.approvedByName ? String(data.approvedByName) : undefined,
    approvedAt: serializeTimestamp(data.approvedAt),
    acceptedBy: data.acceptedBy ? String(data.acceptedBy) : undefined,
    acceptedByName: data.acceptedByName ? String(data.acceptedByName) : undefined,
    acceptedAt: serializeTimestamp(data.acceptedAt),
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeResponsibilityTransfers(
  companyIds: string[],
  onChange: (requests: ResponsibilityTransferRequest[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db || !companyIds.length) {
    onChange([]);
    return () => undefined;
  }

  const requestsByCompany = new Map<string, ResponsibilityTransferRequest[]>();
  const emit = () => {
    onChange(Array.from(requestsByCompany.values()).flat().sort((left, right) => right.createdAt.localeCompare(left.createdAt)));
  };

  const unsubscribes = companyIds.map((companyId) => onSnapshot(
    query(collection(db!, "companies", companyId, "responsibilityTransfers"), orderBy("createdAt", "desc")),
    (snapshot) => {
      requestsByCompany.set(companyId, snapshot.docs.map((transferDoc) => mapTransfer(transferDoc.id, transferDoc.data())));
      emit();
    },
    (error) => onError(error.message)
  ));

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export async function createResponsibilityTransferRequest(input: CreateResponsibilityTransferInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!input.assets.length) {
    throw new Error("Selecione pelo menos um bem para transferir.");
  }

  if (input.assets.some((asset) => asset.companyId !== input.companyId)) {
    throw new Error("A transferencia precisa conter bens de uma unica empresa.");
  }

  const requestRef = doc(collection(db, "companies", input.companyId, "responsibilityTransfers"));
  await writeBatch(db)
    .set(requestRef, {
      companyId: input.companyId,
      assetIds: input.assets.map((asset) => asset.id),
      assetPatrimonyNumbers: input.assets.map((asset) => asset.patrimonyNumber),
      fromUserId: input.fromUserId || null,
      fromUserName: input.fromUserName,
      toUserId: input.toUserId,
      toUserName: input.toUserName,
      toUserEmail: input.toUserEmail || null,
      status: input.requiresManagerApproval ? "aguardando_aprovacao" : "aguardando_confirmacao",
      requiresManagerApproval: input.requiresManagerApproval,
      requestedBy: user.uid,
      requestedByName: userName(user),
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    })
    .commit();
}

export async function approveResponsibilityTransfer(companyId: string, requestId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "responsibilityTransfers", requestId), {
    status: "aguardando_confirmacao",
    approvedBy: user.uid,
    approvedByName: userName(user),
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function rejectResponsibilityTransfer(companyId: string, requestId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "companies", companyId, "responsibilityTransfers", requestId), {
    status: "recusada",
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}

export async function acceptResponsibilityTransfer(request: ResponsibilityTransferRequest, user: User, condition?: ResponsibilityTransferConditionInput): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const requestRef = doc(db, "companies", request.companyId, "responsibilityTransfers", request.id);
  const requestSnapshot = await getDoc(requestRef);
  if (!requestSnapshot.exists() || requestSnapshot.data().status !== "aguardando_confirmacao") {
    throw new Error("Esta transferencia nao esta mais aguardando confirmacao.");
  }

  const batch = writeBatch(db);
  request.assetIds.forEach((assetId) => {
    const assetRef = doc(db!, "companies", request.companyId, "assets", assetId);
    const historyRef = doc(collection(db!, "companies", request.companyId, "assets", assetId, "responsibilityHistory"));
    batch.update(assetRef, {
      responsible: request.toUserName,
      responsibleUserId: request.toUserId,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
    batch.set(historyRef, {
      transferRequestId: request.id,
      fromUserId: request.fromUserId || null,
      fromUserName: request.fromUserName,
      toUserId: request.toUserId,
      toUserName: request.toUserName,
      requestedBy: request.requestedBy,
      requestedByName: request.requestedByName,
      authorizedBy: request.approvedBy || request.requestedBy,
      authorizedByName: request.approvedByName || request.requestedByName,
      acceptedBy: user.uid,
      acceptedByName: userName(user),
      conditionAtTransfer: condition?.conditionAtTransfer ?? null,
      inspectionId: condition?.inspectionId ?? null,
      startedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      createdBy: user.uid
    });
  });
  batch.update(requestRef, {
    status: "concluida",
    acceptedBy: user.uid,
    acceptedByName: userName(user),
    acceptedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
  await batch.commit();
}

export async function directTransferResponsibility(
  companyId: string,
  assets: Asset[],
  target: { userId: string; userName: string; userEmail?: string },
  user: User,
  condition?: ResponsibilityTransferConditionInput
): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  if (!assets.length) {
    throw new Error("Selecione pelo menos um bem para transferir.");
  }

  if (assets.some((asset) => asset.companyId !== companyId)) {
    throw new Error("A transferencia direta precisa conter bens de uma unica empresa.");
  }

  const batch = writeBatch(db);
  assets.forEach((asset) => {
    const assetRef = doc(db!, "companies", companyId, "assets", asset.id);
    const historyRef = doc(collection(db!, "companies", companyId, "assets", asset.id, "responsibilityHistory"));
    batch.update(assetRef, {
      responsible: target.userName,
      responsibleUserId: target.userId,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
    batch.set(historyRef, {
      transferRequestId: null,
      fromUserId: asset.responsibleUserId || null,
      fromUserName: asset.responsible || "",
      toUserId: target.userId,
      toUserName: target.userName,
      toUserEmail: target.userEmail || null,
      requestedBy: user.uid,
      requestedByName: userName(user),
      authorizedBy: user.uid,
      authorizedByName: userName(user),
      acceptedBy: user.uid,
      acceptedByName: userName(user),
      conditionAtTransfer: condition?.conditionAtTransfer ?? null,
      inspectionId: condition?.inspectionId ?? null,
      startedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      createdBy: user.uid
    });
  });
  await batch.commit();
}
